import React, { ComponentType, ReactNode } from "react";
import styled, { CSSObject, keyframes } from "styled-components";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import UpCustomerSettingsInfo from "../up-customer-settings-info";
import globalMessages from "../../messages/globalMessages";

export interface FieldErrorPosition {
  top?: string;
  bottom?: string;
  right?: string;
  left?: string;
}

interface ImmutableFieldError {
  get(key: "message"): string;
  get(key: "value"): string | number;
}

export type FieldError = ReactNode | ImmutableFieldError;

export interface FieldHocProps {
  input: {
    name: string;
  };
  meta: {
    error?: FieldError;
    submitFailed?: boolean;
  };
}

export interface FieldLayoutProps {
  width?: string;
  margin?: string;
  padding?: string;
  styles?: CSSObject;
  label?: ReactNode;
  infoText?: ReactNode;
  infoTextInput?: ReactNode;
  errorIcon?: FieldErrorPosition;
  className?: string;
}

interface InputGroupProps {
  width?: string;
  padding?: string;
  margin?: string;
  styles?: CSSObject;
}

const InputGroup = styled.div<InputGroupProps>`
  display: inline-block;
  width: ${(props) => props.width || "100%"};
  padding: ${(props) => props.padding || "0 10px 0 0"};
  @media only screen and (max-width: ${(props) => props.theme.xsmall}) {
    margin: ${(props) => props.margin || "10px 0 0"};
  }
  ${(props) => props.styles}
`;

const shake = keyframes`
  8%, 41% {
    transform: translateX(-10px);
  }
  25%, 58% {
    transform: translateX(10px);
  }
  75% {
    transform: translateX(-5px);
  }
  92% {
    transform: translateX(5px);
  }
  0%, 100% {
    transform: translateX(0);
  }
`;

const shakeSafari = keyframes`
  8%, 41% {
  -webkit-transform: translateX(-10px);
  }
  25%, 58% {
  -webkit-transform: translateX(10px);
  }
  75% {
  -webkit-transform: translateX(-5px);
  }
  92% {
  -webkit-transform: translateX(5px);
  }
  0%, 100% {
  -webkit-transform: translateX(0);
  }
`;

interface FieldLabelProps {
  htmlFor?: string;
}

const Label = styled.div<FieldLabelProps>`
  margin-left: 3px;
  margin-bottom: 3px;
  font-weight: 500;
  .addNew {
    cursor: pointer;
    color: #029aa3;
    font-weight: 400;
  }
`;

const defaultPosition: FieldErrorPosition = {
  top: "50%",
  right: "5px"
};

interface PositionedErrorProps {
  position: FieldErrorPosition;
}

const ErrorMessage = styled.div<PositionedErrorProps>`
  color: #fff;
  position: absolute;
  top: ${(props) => `calc(${props.position.top} + 2px)`};
  right: ${(props) => `calc(${props.position.right} - 5px)`};
  background: #ac231d;
  padding: 5px 5px;
  box-sizing: border-box;
  font-size: 13px;
  border-radius: 3px;
  transform: translateY(50%);
  z-index: 10;

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    right: -8px;
    top: 1px;
    box-sizing: border-box;
    border: 6px solid black;
    border-color: transparent transparent #ac231d #ac231d;
    transform-origin: 0 0;
    transform: rotate(135deg);
  }
`;

const getPositionStyles = ({
  position: {
    top = defaultPosition.top,
    bottom = "auto",
    right = defaultPosition.right,
    left = "auto"
  }
}: PositionedErrorProps) => `
  position: absolute;
  top: ${top};
  right: ${right};
  bottom: ${bottom};
  left: ${left};
`;

const ErrorIcon = styled(FontAwesomeIcon)<PositionedErrorProps>`
  color: #ac231d;
  background: radial-gradient(#fff 50%, #0000 0%);
  border-radius: 50%;
  transform: translate(0%, -50%);
  z-index: 9;

  ${getPositionStyles};
`;

const InfoIcon = styled.div`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translate(0%, -50%);
  z-index: 9;
`;

const Field = styled.div`
  &.errorAnimation {
    animation: ${shake} 0.5s linear;
    -webkit-animation: ${shakeSafari} 0.5s linear;
  }
`;

const isImmutableFieldError = (error: FieldError): error is ImmutableFieldError =>
  typeof error === "object" &&
  error !== null &&
  "get" in error &&
  typeof error.get === "function" &&
  typeof error.get("message") === "string";

const renderError = (error: FieldError): ReactNode => {
  if (isImmutableFieldError(error)) {
    const message = error.get("message");
    return <FormattedMessage {...globalMessages[message]} values={{ value: error.get("value") }} />;
  }

  if (typeof error === "string" && globalMessages[error]) {
    const errMsg = globalMessages[error];
    return <FormattedMessage {...errMsg} />;
  }
  return error;
};

type ErrorCheck<Meta> = (meta: Meta) => unknown;

const errorAndFailedSubmit = (meta: FieldHocProps["meta"]) => meta.error && meta.submitFailed;

interface ErrorProps {
  position?: FieldErrorPosition;
  error: FieldError;
}

const Error = ({ position = defaultPosition, error }: ErrorProps) => (
  <ErrorMessage position={position}>{renderError(error)}</ErrorMessage>
);

const FieldsHOC = <WrappedProps extends FieldHocProps>(
  WrappedComponent: ComponentType<WrappedProps>,
  checkForError: ErrorCheck<WrappedProps["meta"]> = errorAndFailedSubmit
): ComponentType<WrappedProps & FieldLayoutProps> => {
  type Props = WrappedProps & FieldLayoutProps;

  class WrappedField extends React.PureComponent<Props> {
    state = {
      errorMessageVisible: false
    };

    toggleErrorMessage(val = true): void {
      this.setState({ errorMessageVisible: val });
    }

    render() {
      const {
        meta,
        meta: { error },
        input: { name },
        width,
        margin,
        padding,
        styles,
        label,
        infoText,
        errorIcon,
        infoTextInput,
        className
      } = this.props;
      const position = errorIcon === undefined ? defaultPosition : errorIcon;
      const hasError = () => Boolean(checkForError(meta));

      return (
        <InputGroup
          width={width}
          margin={margin}
          padding={padding}
          styles={styles}
          className={className}
        >
          {label && (
            <Label htmlFor={name}>
              {label}
              {infoText && <UpCustomerSettingsInfo infoText={infoText} />}
            </Label>
          )}
          <Field
            onFocus={() => this.toggleErrorMessage()}
            onBlur={() => this.toggleErrorMessage(false)}
            className={hasError() ? "errorAnimation" : undefined}
          >
            <div style={{ position: error || infoTextInput ? "relative" : undefined }}>
              {hasError() && <ErrorIcon position={position} icon="exclamation-circle" />}
              {!error && infoTextInput && (
                <InfoIcon>
                  <UpCustomerSettingsInfo infoText={infoTextInput} width="180px" />
                </InfoIcon>
              )}
              <WrappedComponent {...this.props} />
              {hasError() && this.state.errorMessageVisible && (
                <Error position={position} error={error} />
              )}
            </div>
          </Field>
        </InputGroup>
      );
    }
  }

  return WrappedField;
};

export default FieldsHOC;
